/**
 * Athlete-focused descriptions for every blood-work parameter the dashboard
 * tracks. Each entry is plain-English and intentionally short so it fits well
 * in a tooltip. Keep this file in sync with parser.REFERENCE_RANGES.
 *
 * Sources: standard clinical reference texts + IOC/ACSM sports-medicine notes.
 */

export const PARAM_INFO = {
  // ---- Iron Profile -------------------------------------------------------
  iron: {
    name: "Iron",
    short: "Iron carried in serum, mostly bound to transferrin.",
    why: "Low values impair oxygen delivery → endurance drops, recovery slows. Very common deficiency in athletes (heavy sweating, foot-strike haemolysis, GI losses).",
  },
  hb: {
    name: "Haemoglobin (Hb)",
    short: "Oxygen-carrying protein inside red cells.",
    why: "Direct indicator of aerobic capacity. Even mild dips (≤12 g/dl) flag anaemia and a noticeable drop in VO₂max.",
  },
  ferritin: {
    name: "Ferritin",
    short: "Body's iron-storage protein — measured in serum.",
    why: "Best early warning for iron depletion. Endurance athletes should stay >35 ng/ml even if Hb still looks normal.",
  },
  ts: {
    name: "Transferrin Saturation",
    short: "% of transferrin carrying iron right now.",
    why: "Pairs with ferritin to spot iron-deficient erythropoiesis. <15% means production of new red cells is being throttled.",
  },

  // ---- Vitamins -----------------------------------------------------------
  b9: {
    name: "Vitamin B9 (Folate)",
    short: "Cofactor for DNA synthesis and red-cell maturation.",
    why: "Low folate causes megaloblastic anaemia and elevated homocysteine. Common in athletes on restrictive diets.",
  },
  b12: {
    name: "Vitamin B12 (Cobalamin)",
    short: "Essential for red-cell production and nerve function.",
    why: "Deficiency presents as fatigue, tingling, anaemia. Strict vegetarians (most YCH athletes) are at higher risk.",
  },
  vit_d: {
    name: "Vitamin D",
    short: "Steroid hormone — bone, muscle, immune function.",
    why: "<30 ng/ml linked to stress fractures, muscle weakness, more URTIs. Surprisingly common despite sunny climates.",
  },

  // ---- Glycaemic ----------------------------------------------------------
  hba1c: {
    name: "HbA1c",
    short: "Glycated haemoglobin — average blood-sugar over ~3 months.",
    why: ">5.6% suggests prolonged hyperglycaemia. Useful screen even in lean young athletes with family history of diabetes.",
  },

  // ---- Lipid panel --------------------------------------------------------
  cholesterol: {
    name: "Total Cholesterol",
    short: "All cholesterol fractions in serum.",
    why: "Mostly diet-driven. High values flag cardiovascular risk; trends matter more than a single reading.",
  },
  tgl: {
    name: "Triglycerides",
    short: "Fats circulating in blood.",
    why: "Rises after fatty meals and with high refined-carb intake. Spikes can mask in fasted athletes.",
  },
  hdl: {
    name: "HDL Cholesterol",
    short: "\"Good\" cholesterol — moves cholesterol back to the liver.",
    why: "Higher is protective; endurance training reliably pushes HDL up. <40 mg/dl is a flag.",
  },
  ldl: {
    name: "LDL Cholesterol",
    short: "\"Bad\" cholesterol — deposits in artery walls.",
    why: "Genetics + diet driven. Aim <110 mg/dl for athletes with any cardiovascular history.",
  },

  // ---- Thyroid ------------------------------------------------------------
  t3: {
    name: "Triiodothyronine (T3)",
    short: "Active thyroid hormone — metabolic rate driver.",
    why: "Low T3 with heavy training volume suggests over-training syndrome / low-energy availability.",
  },
  t4: {
    name: "Thyroxine (T4)",
    short: "Pro-hormone — converted to T3 in tissues.",
    why: "Useful alongside TSH to spot under-active thyroid; symptoms include cold intolerance, weight gain, sluggishness.",
  },
  tsh: {
    name: "TSH",
    short: "Pituitary signal that drives thyroid output.",
    why: "Master regulator. High = under-active thyroid. Low = over-active. Most sensitive single thyroid test.",
  },

  // ---- Calcium / parathyroid ---------------------------------------------
  pth: {
    name: "Parathyroid Hormone (PTH)",
    short: "Regulates blood calcium by acting on bone & kidneys.",
    why: "Elevated PTH with low vitamin D signals chronic calcium-vitamin D insufficiency → bone-density risk.",
  },
  calcium: {
    name: "Calcium",
    short: "Total serum calcium.",
    why: "Tightly regulated. Deviations point to parathyroid, vitamin D, or kidney issues — rarely just diet.",
  },
  phosphorus: {
    name: "Phosphorus",
    short: "Inorganic phosphate in serum — bone, ATP, cell membranes.",
    why: "Low values are common with rapid weight loss, refeeding, and chronic carb-heavy diets. Affects energy metabolism.",
  },
  magnesium: {
    name: "Magnesium",
    short: "Cofactor for >300 enzymes — muscle, nerve, ATP production.",
    why: "Heavy sweating depletes Mg. Cramps, palpitations, sleep issues are typical early signs.",
  },

  // ---- Kidney function ----------------------------------------------------
  bun: {
    name: "Blood Urea Nitrogen (BUN)",
    short: "Nitrogen from urea, the body's protein-waste end-product.",
    why: "High in dehydration or high-protein diets; low can flag liver dysfunction.",
  },
  uric_acid: {
    name: "Uric Acid",
    short: "Purine breakdown product.",
    why: "Elevated values may indicate gout risk, kidney stress, or excessive purine intake (red meat, alcohol).",
  },
  urea: {
    name: "Urea",
    short: "Liver's main route for disposing of excess nitrogen.",
    why: "Mirror of BUN. Rises with dehydration, intense catabolism, high protein intake.",
  },
  creatinine: {
    name: "Creatinine",
    short: "Muscle breakdown product — filtered out by kidneys.",
    why: "Athletes' values run a bit higher because of muscle mass. Trending up over time suggests reduced kidney function.",
  },

  // ---- Liver function -----------------------------------------------------
  bilirubin: {
    name: "Bilirubin",
    short: "Pigment from old red cells — cleared by the liver.",
    why: "High can mean liver stress, Gilbert's syndrome (benign), or haemolysis from intense exercise.",
  },
  protein: {
    name: "Total Protein",
    short: "Albumin + globulins in serum.",
    why: "Low protein flags malnutrition or liver issues; usually moves slowly.",
  },
  albumin: {
    name: "Albumin",
    short: "Most abundant blood protein — made by the liver.",
    why: "Drops in chronic illness, severe under-eating, or sustained inflammation.",
  },
  sgot: {
    name: "SGOT / AST",
    short: "Enzyme leaked when liver or muscle cells are stressed.",
    why: "Athletes' values normally run high after heavy training — not always a liver issue. Pair with SGPT for clarity.",
  },
  sgpt: {
    name: "SGPT / ALT",
    short: "Enzyme more specific to liver damage.",
    why: "Sustained elevation needs investigation; transient bumps after very heavy training are usually benign.",
  },
  alp: {
    name: "Alkaline Phosphatase (ALP)",
    short: "Enzyme from liver, bone, and gut.",
    why: "High values in growing teenagers are normal (bone remodelling). Persistent high in adults → liver/bone workup.",
  },
  ggt: {
    name: "Gamma-GT (GGT)",
    short: "Liver enzyme — most sensitive marker for alcohol or biliary stress.",
    why: "Most useful liver enzyme for screening; elevations track closely with alcohol intake.",
  },

  // ---- Inflammation -------------------------------------------------------
  esr: {
    name: "ESR (Sedimentation rate)",
    short: "How fast red cells settle in a tube — non-specific inflammation marker.",
    why: "Rises slowly with chronic inflammation, infection, anaemia. Useful to track over months, not days.",
  },
  crp: {
    name: "CRP (C-reactive protein)",
    short: "Acute-phase protein — fast-rising inflammation marker.",
    why: "Spikes with infection or heavy training (rhabdomyolysis). Should normalise within days.",
  },
  cpk: {
    name: "CPK / Creatine Kinase",
    short: "Enzyme leaked by damaged muscle cells.",
    why: "Expected to be elevated after hard sessions. Very high values (>1000) with dark urine → suspect rhabdomyolysis.",
  },

  // ---- Biometrics ---------------------------------------------------------
  height: {
    name: "Height",
    short: "Standing height measured without footwear.",
    why: "For junior athletes, growth velocity is a key signal — under-fuelling and over-training slow it down. Adult height is also boat-class-relevant (e.g. taller sailors carry an advantage in trapeze classes).",
  },
  weight: {
    name: "Weight",
    short: "Body mass on calibrated scales, ideally same time of day.",
    why: "Weekly trend matters more than the absolute number. Sustained drop with normal training load → calorie deficit. Sudden gain → fluid retention or growth spurt.",
  },
  bmi: {
    name: "BMI",
    short: "Weight (kg) ÷ height² (m²) — a quick body-mass proxy.",
    why: "Adult zones: <18.5 underweight, 18.5–24.9 normal, 25–29.9 overweight, ≥30 obese. For junior athletes BMI underestimates muscle, so use it alongside the growth chart.",
  },
};

export function infoFor(key) {
  return PARAM_INFO[key] || null;
}
